// Document verification uploader.
// Reads a chosen file as base64, POSTs to /api/verify, surfaces the AI result.
// Reused by:
//   - MLS stage (type=ownership) — gates "Submit to MLS"
//   - Prep stage (type=inspection) — pulls structured inspection findings
//     into journey.stages.prep.data.inspection_findings so DisclosuresStage
//     can pre-fill automatically.

import { useState } from "react";
import Spinner from "./Spinner";
import { BETA_SECRET, track } from "../lib/api";

const MAX_BYTES = 4 * 1024 * 1024; // 4MB

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = r.result || "";
      const i = String(s).indexOf(",");
      resolve(i >= 0 ? String(s).slice(i + 1) : String(s));
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function DocVerification({
  type,                     // "ownership" | "inspection"
  journey,
  expectedName,
  expectedAddress,
  onComplete,               // (result) => void
  accept,                   // file accept attr override
  label,
  helpText,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    setErr(null);
    if (!f) { setFile(null); return; }
    if (f.size > MAX_BYTES) {
      setErr(`File too large (max ${(MAX_BYTES / 1024 / 1024).toFixed(0)} MB).`);
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr(null); setResult(null);
    track("seller_verification_upload", { type, mime: file.type, size: file.size });
    try {
      const b64 = await readAsBase64(file);
      const res = await fetch("/api/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({
          type,
          file_base64: b64,
          file_mime: file.type,
          file_name: file.name,
          journey_id: journey?.id || null,
          expected_name: expectedName || null,
          expected_address: expectedAddress || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `verify ${res.status}`);
      setResult(data);
      onComplete?.(data);
      track("seller_verification_result", {
        type, status: data.status,
        name_match: data.name_match, address_match: data.address_match,
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const acceptAttr = accept || (type === "ownership"
    ? ".pdf,image/jpeg,image/png,image/heic"
    : ".pdf,image/jpeg,image/png");

  return (
    <div style={{ background: "#fff", border: "1.5px solid #d8ede6", borderRadius: 12, padding: 16 }}>
      {label && (
        <div style={{ fontWeight: 700, color: "#152d21", fontSize: 14, marginBottom: 4 }}>{label}</div>
      )}
      {helpText && (
        <div style={{ fontSize: 12, color: "#5b7365", lineHeight: 1.5, marginBottom: 10 }}>{helpText}</div>
      )}

      {!result && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="file"
              accept={acceptAttr}
              onChange={onPick}
              disabled={busy}
              style={{ fontSize: 13, fontFamily: "inherit", color: "#1c2b25", flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              className="s-btn amber"
              onClick={upload}
              disabled={!file || busy}
            >
              {busy ? "Verifying…" : "Upload"}
            </button>
          </div>
          {busy && <div style={{ marginTop: 12 }}><Spinner label={type === "ownership" ? "Reading document…" : "Parsing inspection report…"} /></div>}
          {err && <div className="s-intake-err" style={{ marginTop: 10 }}>{err}</div>}
        </>
      )}

      {result && (
        <ResultDisplay
          type={type}
          result={result}
          expectedName={expectedName}
          expectedAddress={expectedAddress}
          onRetry={() => { setResult(null); setFile(null); }}
        />
      )}
    </div>
  );
}

function ResultDisplay({ type, result, expectedName, expectedAddress, onRetry }) {
  if (type === "ownership") {
    const ok = result.status === "approved";
    return (
      <div>
        <div style={{
          display: "inline-block", padding: "4px 12px", borderRadius: 12,
          fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
          marginBottom: 12,
          background: ok ? "#e4f7ef" : "#fef4e8",
          color: ok ? "#2d7a62" : "#c47c1a",
          border: `1px solid ${ok ? "#b8e8d4" : "#f1d6a8"}`,
        }}>
          {ok ? "✓ Verified" : "⚠ Needs manual review"}
        </div>
        <div style={{ fontSize: 13, color: "#1c2b25", lineHeight: 1.7 }}>
          <div><strong style={{ color: "#152d21" }}>Name on doc:</strong> {result.extracted_name || "—"}{" "}
            <span style={{ color: result.name_match ? "#2d7a62" : "#c0354a" }}>
              {result.name_match ? "✓ matches" : `× doesn't match "${expectedName || "(no expected)"}"`}
            </span>
          </div>
          <div><strong style={{ color: "#152d21" }}>Address on doc:</strong> {result.extracted_address || "—"}{" "}
            <span style={{ color: result.address_match ? "#2d7a62" : "#c0354a" }}>
              {result.address_match ? "✓ matches" : `× doesn't match`}
            </span>
          </div>
        </div>
        {!ok && (
          <p style={{ fontSize: 12, color: "#9aa9a0", marginTop: 10, lineHeight: 1.5 }}>
            We'll flag this for an admin to review. You can also upload a different document.
          </p>
        )}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="s-link-btn" onClick={onRetry}>upload different document</button>
        </div>
      </div>
    );
  }

  // inspection
  const f = result.findings || {};
  const rows = [
    ["Roof",        f.roof],
    ["Electrical",  f.electrical],
    ["Plumbing",    f.plumbing],
    ["Foundation",  f.foundation],
    ["HVAC",        f.hvac],
  ];
  const condColor = (c) => ({ good: "#2d7a62", fair: "#c47c1a", poor: "#c0354a", "n/a": "#9aa9a0" }[c] || "#9aa9a0");
  return (
    <div>
      <div style={{
        display: "inline-block", padding: "4px 12px", borderRadius: 12,
        fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
        marginBottom: 14, background: "#e4f7ef", color: "#2d7a62", border: "1px solid #b8e8d4",
      }}>
        ✓ Findings parsed
      </div>
      {rows.map(([label, r]) => r && (
        <div key={label} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f0f4f2" }}>
          <div style={{ flexShrink: 0, width: 90, fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#9aa9a0", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#1c2b25" }}>
            <span style={{ color: condColor(r.condition), fontWeight: 700, marginRight: 8 }}>{(r.condition || "—").toUpperCase()}</span>
            <span style={{ color: "#5b7365" }}>{r.notes}</span>
          </div>
        </div>
      ))}
      {f.material_defects?.length > 0 && (
        <div style={{ marginTop: 12, background: "#fef4e8", border: "1px solid #f1d6a8", borderRadius: 10, padding: 10 }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#c47c1a", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
            Material defects (must disclose)
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#1c2b25", lineHeight: 1.65 }}>
            {f.material_defects.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <button type="button" className="s-link-btn" onClick={onRetry}>upload a different report</button>
      </div>
    </div>
  );
}
